import type { DestroyOptions, Sprite } from 'pixi.js';
import { Container, Rectangle, Point } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedValue } from '@softgames/localization';
import type { AppStateController } from '../../../../app/state/AppStateController';
import type { CounterViewLayout } from '../../../../libs/layouts/types/LayoutShapes';

export type CounterConfig = {
  initListener: (state: AppStateController, instance: CounterView) => void;
};

export class CounterView extends Container {
  readonly #counterLayout: CounterViewLayout;

  public updateEnabled = true;

  private amount = 0;

  public readonly onClicked = new Signal<(counter: CounterView) => void>();

  constructor(
    private readonly config: CounterConfig,
    layout: CounterViewLayout,
    state: AppStateController | null,
  ) {
    super();

    this.#counterLayout = layout;
    this.addChild(this.#counterLayout.root);

    this.init(state);
  }

  public destroy(options?: DestroyOptions): void {
    this.onClicked.disconnectAll();
    super.destroy(options);
  }

  private init(state: AppStateController | null): this {
    if (this.destroyed) return this;

    const { background } = this.#counterLayout;

    this.interactive = true;
    this.interactiveChildren = true;
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Rectangle(background.x, background.y, background.width, background.height);
    this.on('pointertap', () => {
      this.onClicked.emit(this);
    });

    if (state) this.addStateListener(state);

    return this;
  }

  public getIconGlobalPosition(): Point {
    if (this.counterLayout.icon.destroyed) return new Point();
    return this.counterLayout.icon.getGlobalPosition();
  }

  public addStateListener(state: AppStateController): void {
    if (state.subscriptions.contextHasSubscriptions(this)) return;
    this.config.initListener(state, this);
  }

  public getDisplayedAmount(): number {
    return this.amount;
  }

  public updateAmount(amount: number): this {
    if (this.destroyed || !this.updateEnabled) {
      return this;
    }
    this.amount = amount;
    this.#counterLayout.counterText.text = getLocalizedValue(amount);

    return this;
  }

  get counterLayout(): CounterViewLayout {
    return this.#counterLayout;
  }

  get icon(): Sprite {
    return this.#counterLayout.icon;
  }
}
